import './publicPath';
import React, { useEffect } from 'react';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom';
import { configure } from 'mobx';
import 'jszip';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
// import 'react-tooltip/dist/react-tooltip.css';

import { App } from './chaika/App';
import { SENTRY_ALREADY_REPORTED } from './lib/axios/axios';
import { assertIsDefined } from '@utils/assertIsDefined';

// Skip on local and e2e hosts so production builds served there don't report to Sentry.
const isLocalOrE2eHost = ['localhost', '127.0.0.1', '::1', 'frontend'].includes(window.location.hostname);

if (process.env.NODE_ENV === 'production' && !isLocalOrE2eHost) {
    const skippedPathForTracing: string[] = ['/user-not-found', '/template'];
    Sentry.init({
        dsn: 'https://bfa5e30cb898eee53d845a7af9505e41@sentry.chaika.com/3',
        attachStacktrace: true,
        ignoreErrors: [
            /Network Error/,
            /Network Error; Network Error/,
            /^\[axios\] network error/,
            /^\[axios\] timeout PUT \/api\/auth\/from/,
            /^\[axios\] timeout/,
            /Request failed with status code 403/,
            /NotFound: User not found: email =/,
            /Conflict: Письмо уже отправлено на email/,
            /Conflict: Неверный пароль/,
            // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
            // reduce this. (thanks @acdha)
            // See http://stackoverflow.com/questions/4113268
            'bmi_SafeAddOnload',
            'EBCallBackMessageReceived',
            '/Extension context invalidated/',
            /find variable: gmo/,
        ],
        denyUrls: [
            // Chrome extensions
            /extensions\//i,
            /chrome-extension\//i,
            /dev.cms.chaika.com\//i,
            /qa.cms.chaika.com\//i,
            /chrome-extension\//i,
            /^chrome:\/\//i,
        ],
        integrations: [
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
        ],
        beforeSend(event, hint) {
            const original = hint?.originalException as Record<string, unknown> | undefined;
            if (original && original[SENTRY_ALREADY_REPORTED]) {
                return null;
            }
            return event;
        },
        tracesSampler: ({ location, name }) => {
            const path = location?.href.replace('https://cms.chaika.com', '') ?? name;
            if (path === '/' || skippedPathForTracing.some(skipPath => path.includes(skipPath))) {
                return 0;
            }

            return 0.7;
        },
        tracesSampleRate: 0.1,
        release: process.env.SENTRY_RELEASE_VERSION,
    });
}

configure({
    enforceActions: 'never',
});

const container = document.getElementById('app');
assertIsDefined(container, 'index.html must contain the #app root element');
const root = createRoot(container);

root.render(React.createElement(App));
